import {
  ArrowClockwise, ArrowDownRight, ArrowLineDown, ArrowUpRight, ArrowsClockwise,
  CaretDown, ChartPieSlice, Check, CheckCircle, CurrencyCircleDollar,
  DownloadSimple, Funnel, Handbag, Package, Storefront, Tag, User, WarningCircle, X,
} from '@phosphor-icons/react'
import type { Icon, IconWeight } from '@phosphor-icons/react'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties, KeyboardEvent as ReactKeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { download, request, withStore } from '../api/client'
import type { StoreOption } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { QueryState } from '../components/States'
import { LogCenterPanel } from './LogCenterPanel'

interface Snapshot {
  salesAmount: string; salesQuantity: number; profit: string; intakeAmount: string
  recoveryQuantity: number; consignmentQuantity: number
}
interface Statistics {
  period: string; current: Snapshot; previous: Snapshot; salesChangePercent?: string
  profitChangePercent?: string; intakeChangePercent?: string; overdueCount: number
}
interface Rank { id: string | number; name: string; currency: string; amount: string; quantity: number }
interface InventoryBreakdown {
  groupId?: string | number; name?: string; totalCount: number; totalValue: string; other?: boolean
}
interface CategoryOption { id: string | number; name: string }
interface CatalogBootstrap { categories?: CategoryOption[] }
interface Reports {
  inventorySummary?: {
    totalCount: number; saleValue: string; costAmount?: string | null; ownCost?: string | null
    dimension?: InventoryDimension; breakdown?: InventoryBreakdown[]
  }
  inventoryHealth?: {
    lockedCount: number; borrowedCount: number; offShelfCount: number; overdueCount: number
    ownCost?: string | null; consignmentValue?: string; totalCount: number
    within30Days: number; days31To90: number; over90Days: number
  }
  storeSalesRank?: Rank[]; storeRecoveryRank?: Rank[]
  employeeSalesRank?: Rank[]; employeeRecoveryRank?: Rank[]
}
interface TrendItem { bucket: string; amount: string; profit: string; quantity: number }
interface Trend { items: TrendItem[] }

type TrendPeriod = 'TODAY' | 'MONTH' | 'LAST_MONTH'
type ReportPeriod = 'MONTH' | 'LAST_MONTH' | 'TODAY'
type InventoryDimension = 'STORE' | 'CATEGORY'
type RankType = 'employeeSalesRank' | 'employeeRecoveryRank' | 'storeSalesRank' | 'storeRecoveryRank'
type HealthFilterSection = 'store' | 'category'

interface HealthFilters { storeId: string; categoryId: string }

interface MenuOption<T extends string> { value: T; label: string }
interface ReportMenuProps<T extends string> {
  value: T
  options: Array<MenuOption<T>>
  onChange: (value: T) => void
  ariaLabel: string
  icon?: Icon
  iconSize?: number
  iconWeight?: IconWeight
}

const number = new Intl.NumberFormat('zh-CN', { maximumFractionDigits: 0 })
const currencyPrefix = (currency: string) => currency === 'CNY' ? '¥' : currency === 'HKD' ? 'HK$' : '₮'
const money = (value?: string | null, currency = 'CNY') => `${currencyPrefix(currency)}${number.format(Number(value || 0))}`
const reportPeriodOptions: Array<MenuOption<ReportPeriod>> = [
  { value: 'MONTH', label: '本月' },
  { value: 'LAST_MONTH', label: '上月' },
  { value: 'TODAY', label: '今日' },
]
const inventoryDimensionOptions: Array<MenuOption<InventoryDimension>> = [
  { value: 'STORE', label: '按门店' },
  { value: 'CATEGORY', label: '按分类' },
]
const inventoryColors = [
  '#647df0', '#8fa3ff', '#8b66db', '#55a8a4', '#e3a24f', '#df7893',
  '#5d9ad6', '#9b7cdf', '#58a574', '#cf8153', '#7187ad', '#bd72b7',
]
const inventoryColor = (index: number) => inventoryColors[index]
  || `hsl(${Math.round((index * 137.508 + 222) % 360)} 58% ${index % 2 ? 58 : 68}%)`

function useReports(
  period: ReportPeriod,
  inventoryDimension: InventoryDimension,
  storeId: string,
  healthStoreId = '',
  healthCategoryId = '',
) {
  const currency = 'CNY'
  const storeHeader = storeId || 'ALL'
  return useQuery({
    queryKey: ['home-reports', period, currency, storeId, inventoryDimension, healthStoreId, healthCategoryId],
    queryFn: () => request<Reports>(withStore({
      url: '/luxury/home/reports',
      params: {
        period,
        currency,
        storeId: storeId || undefined,
        inventoryDimension,
        healthStoreId: healthStoreId || undefined,
        healthCategoryId: healthCategoryId || undefined,
      },
    }, storeHeader)),
  })
}

function ReportMenu<T extends string>({
  value, options, onChange, ariaLabel, icon: MenuIcon, iconSize = 13, iconWeight = 'duotone',
}: ReportMenuProps<T>) {
  const [open, setOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const current = options.find((item) => item.value === value) || options[0]!

  useEffect(() => {
    if (!open) return
    const close = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', close)
    return () => document.removeEventListener('pointerdown', close)
  }, [open])

  useEffect(() => {
    if (open) menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitemradio"][aria-checked="true"]')?.focus()
  }, [open])

  const closeAndFocusTrigger = () => {
    setOpen(false)
    triggerRef.current?.focus()
  }
  const choose = (nextValue: T) => {
    onChange(nextValue)
    closeAndFocusTrigger()
  }
  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape' && open) {
      event.preventDefault()
      closeAndFocusTrigger()
      return
    }
    if (!open || !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitemradio"]') || [])
    if (!items.length) return
    event.preventDefault()
    const activeIndex = Math.max(0, items.indexOf(document.activeElement as HTMLButtonElement))
    const nextIndex = event.key === 'Home' ? 0
      : event.key === 'End' ? items.length - 1
        : event.key === 'ArrowDown' ? (activeIndex + 1) % items.length
          : (activeIndex - 1 + items.length) % items.length
    items[nextIndex]?.focus()
  }

  return <div className={`report-menu ${open ? 'is-open' : ''}`} ref={menuRef} onKeyDown={handleKeyDown}>
    <button
      ref={triggerRef}
      className="report-menu-trigger"
      type="button"
      aria-label={`${ariaLabel}：${current.label}`}
      aria-haspopup="menu"
      aria-expanded={open}
      onClick={() => setOpen((currentOpen) => !currentOpen)}
    >
      {MenuIcon && <MenuIcon size={iconSize} weight={iconWeight} />}{current.label}<CaretDown size={11} weight="bold" />
    </button>
    {open && <div className="report-menu-popover" role="menu" aria-label={ariaLabel}>
      {options.map((option) => <button
        className={option.value === value ? 'active' : ''}
        type="button"
        role="menuitemradio"
        aria-checked={option.value === value}
        key={option.value}
        onClick={() => choose(option.value)}
      >{option.label}{option.value === value && <Check size={13} weight="bold" />}</button>)}
    </div>}
  </div>
}

function TrendCanvas({ items }: { items: TrendItem[] }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const render = () => {
      const rect = canvas.getBoundingClientRect()
      const ratio = Math.max(1, window.devicePixelRatio || 1)
      canvas.width = Math.max(1, Math.round(rect.width * ratio))
      canvas.height = Math.max(1, Math.round(rect.height * ratio))
      const context = canvas.getContext('2d')
      if (!context) return
      context.scale(ratio, ratio)
      const width = rect.width; const height = rect.height
      const pad = { left: 44, right: 16, top: 15, bottom: 26 }
      const chartWidth = Math.max(1, width - pad.left - pad.right)
      const chartHeight = Math.max(1, height - pad.top - pad.bottom)
      const rows = items.length ? items : [{ bucket: '—', amount: '0', profit: '0', quantity: 0 }]
      const maximum = Math.max(1, ...rows.flatMap((item) => [Number(item.amount), Number(item.profit)]))
      context.clearRect(0, 0, width, height)
      context.font = '9px PuHui, sans-serif'
      context.lineWidth = 1
      for (let index = 0; index <= 4; index += 1) {
        const y = pad.top + chartHeight * index / 4
        context.strokeStyle = '#dfe5f0'; context.beginPath(); context.moveTo(pad.left, y); context.lineTo(width - pad.right, y); context.stroke()
        context.fillStyle = '#8793a8'; context.textAlign = 'right'; context.fillText(`${number.format(maximum * (4 - index) / 4 / 10000)}万`, pad.left - 8, y + 3)
      }
      const point = (item: TrendItem, index: number, key: 'amount' | 'profit') => ({
        x: pad.left + (rows.length === 1 ? chartWidth / 2 : chartWidth * index / (rows.length - 1)),
        y: pad.top + chartHeight - Number(item[key]) / maximum * chartHeight,
      })
      const drawLine = (key: 'amount' | 'profit', color: string, dashed = false) => {
        context.strokeStyle = color; context.lineWidth = key === 'amount' ? 2.4 : 1.6; context.setLineDash(dashed ? [5, 5] : [])
        context.beginPath(); rows.forEach((item, index) => { const p = point(item, index, key); if (index) context.lineTo(p.x, p.y); else context.moveTo(p.x, p.y) }); context.stroke(); context.setLineDash([])
      }
      drawLine('amount', '#647df0'); drawLine('profit', '#38a17a', true)
      context.fillStyle = '#7f8ba2'; context.textAlign = 'center'
      const indexes = rows.length > 8 ? [0, Math.floor((rows.length - 1) / 4), Math.floor((rows.length - 1) / 2), Math.floor((rows.length - 1) * 3 / 4), rows.length - 1] : rows.map((_, index) => index)
      indexes.forEach((index) => { const p = point(rows[index]!, index, 'amount'); context.fillText(rows[index]!.bucket, p.x, height - 7) })
    }
    const observer = new ResizeObserver(render); observer.observe(canvas); render()
    return () => observer.disconnect()
  }, [items])
  return <canvas ref={canvasRef} role="img" aria-label="销售额与利润趋势" />
}

export function DashboardPage() {
  const { stores, hasPermission } = useAuth()
  const [storeId, setStoreId] = useState('')
  const [trendPeriod, setTrendPeriod] = useState<TrendPeriod>('TODAY')
  const [inventoryDimension, setInventoryDimension] = useState<InventoryDimension>('STORE')
  const [healthFilters, setHealthFilters] = useState<HealthFilters>({ storeId: '', categoryId: '' })
  const [rankingPeriods, setRankingPeriods] = useState<Record<RankType, ReportPeriod>>({
    employeeSalesRank: 'MONTH', employeeRecoveryRank: 'MONTH',
    storeSalesRank: 'MONTH', storeRecoveryRank: 'MONTH',
  })
  const currency = 'CNY'
  const storeHeader = storeId || 'ALL'
  const scope = storeId ? 'CURRENT_STORE' : hasPermission('luxury:home-reports:all-stores-stats') ? 'ALL_STORES' : 'PERSONAL'
  const statistics = useQuery({
    queryKey: ['home-statistics', trendPeriod, storeId],
    queryFn: () => request<Statistics>(withStore({
      url: '/luxury/home/statistics',
      params: { period: trendPeriod === 'LAST_MONTH' ? 'MONTH' : trendPeriod, currency, scope },
    }, storeHeader)),
  })
  const trend = useQuery({
    queryKey: ['home-trend', trendPeriod, storeId],
    queryFn: () => request<Trend>(withStore({
      url: '/luxury/home/sales-trend',
      params: { period: trendPeriod, currency, storeId: storeId || undefined },
    }, storeHeader)),
  })
  const inventoryReports = useReports('MONTH', inventoryDimension, storeId)
  const healthStoreId = healthFilters.storeId && healthFilters.storeId !== storeId ? healthFilters.storeId : ''
  const healthReports = useReports('MONTH', 'STORE', storeId, healthStoreId, healthFilters.categoryId)
  const employeeSalesReports = useReports(rankingPeriods.employeeSalesRank, 'STORE', storeId)
  const employeeRecoveryReports = useReports(rankingPeriods.employeeRecoveryRank, 'STORE', storeId)
  const storeSalesReports = useReports(rankingPeriods.storeSalesRank, 'STORE', storeId)
  const storeRecoveryReports = useReports(rankingPeriods.storeRecoveryRank, 'STORE', storeId)
  const storeOptions = useMemo<Array<MenuOption<string>>>(() => [
    { value: '', label: '全部门店' },
    ...(stores?.availableStores || []).map((store) => ({ value: String(store.id), label: store.name })),
  ], [stores?.availableStores])
  const catalog = useQuery({
    queryKey: ['catalog-bootstrap'],
    queryFn: () => request<CatalogBootstrap>({ url: '/luxury/product/catalog/bootstrap' }),
  })
  const changePageStore = (nextStoreId: string) => {
    setStoreId(nextStoreId)
    setHealthFilters((current) => ({ ...current, storeId: nextStoreId }))
  }
  const setRankingPeriod = (type: RankType, period: ReportPeriod) => {
    setRankingPeriods((currentPeriods) => ({ ...currentPeriods, [type]: period }))
  }
  const exportReport = () => download(
    '/luxury/product/catalog/export',
    { statusGroup: 'ALL', pageNo: 1, pageSize: 100, columns: ['code', 'title', 'brandName', 'salePrice', 'inventoryStatus', 'locationName', 'intakeTime'] },
    `经营库存-${new Date().toISOString().slice(0, 10)}.xlsx`,
    storeHeader,
  )
  const current = statistics.data?.current
  const metrics = useMemo(() => [
    { label: '销售额', value: Number(current?.salesAmount || 0), prefix: '¥', icon: CurrencyCircleDollar, change: statistics.data?.salesChangePercent },
    { label: '销售数量', value: current?.salesQuantity || 0, suffix: '件', icon: Handbag },
    { label: '利润', value: Number(current?.profit || 0), prefix: '¥', icon: ChartPieSlice, change: statistics.data?.profitChangePercent },
    { label: '入库金额', value: Number(current?.intakeAmount || 0), prefix: '¥', icon: ArrowLineDown, change: statistics.data?.intakeChangePercent },
    { label: '回收数量', value: current?.recoveryQuantity || 0, suffix: '件', icon: ArrowsClockwise },
    { label: '寄卖数量', value: current?.consignmentQuantity || 0, suffix: '件', icon: Tag },
  ], [current, statistics.data])

  return <main className="dashboard-page">
    <section className="page-head"><div><h1>经营首页</h1></div><div className="page-actions"><div className="page-store-scope"><span>数据范围</span><ReportMenu value={storeId} options={storeOptions} onChange={changePageStore} ariaLabel="选择经营门店" icon={Storefront} iconSize={16} iconWeight="bold" /></div><button type="button" onClick={exportReport}><DownloadSimple size={16} />导出报表</button></div></section>
    <QueryState loading={statistics.isLoading} error={statistics.error} onRetry={() => statistics.refetch()} />
    {statistics.data && <section className="hero" aria-label="经营数据总览">
      <div className="metric-grid">{metrics.map(({ label, value, prefix, suffix, icon: MetricIcon, change }) => <article className="hero-metric" key={label}><span className="hero-metric-icon"><MetricIcon size={23} weight="duotone" /></span><span className="hero-metric-copy"><small>{label}</small><span className="hero-metric-value">{prefix && <i className="metric-affix metric-prefix">{prefix}</i>}<b className="metric-number">{number.format(value)}</b>{suffix && <i className="metric-affix metric-unit">{suffix}</i>}</span>{change !== undefined && <em>{Number(change) >= 0 ? '较昨日' : '较上期'} {Number(change) >= 0 ? <ArrowUpRight size={10} weight="bold" /> : <ArrowDownRight size={10} weight="bold" />}{Math.abs(Number(change))}%</em>}</span></article>)}</div>
      <div className="chart-panel"><header className="chart-head"><div><strong>{trendPeriod === 'TODAY' ? '今日经营趋势' : trendPeriod === 'MONTH' ? '本月经营趋势' : '上月经营趋势'}</strong><span className="chart-legend"><span className="chart-legend-item"><i className="solid-line" />销售额</span><span className="chart-legend-item"><i className="dash-line" />利润</span></span></div><span className="period-switch" aria-label="趋势周期"><button className={trendPeriod === 'TODAY' ? 'active' : ''} onClick={() => setTrendPeriod('TODAY')}>今日</button><button className={trendPeriod === 'MONTH' ? 'active' : ''} onClick={() => setTrendPeriod('MONTH')}>本月</button><button className={trendPeriod === 'LAST_MONTH' ? 'active' : ''} onClick={() => setTrendPeriod('LAST_MONTH')}>上月</button></span></header><div className="chart-canvas">{trend.isLoading ? <span className="spinner" /> : trend.error ? <button className="chart-retry" type="button" onClick={() => trend.refetch()}><ArrowClockwise size={15} />趋势加载失败，点击重试</button> : <TrendCanvas items={trend.data?.items || []} />}</div></div>
    </section>}
    <section className="lower-dashboard" aria-label="经营报表与动态">
      <div className="reports-grid">
        <InventoryCard summary={inventoryReports.data?.inventorySummary} dimension={inventoryDimension} onDimensionChange={setInventoryDimension} loading={inventoryReports.isLoading} error={inventoryReports.error} resolved={inventoryReports.isSuccess} onRetry={() => inventoryReports.refetch()} />
        <HealthCard
          health={healthReports.data?.inventoryHealth}
          loading={healthReports.isLoading}
          error={healthReports.error}
          resolved={healthReports.isSuccess}
          onRetry={() => healthReports.refetch()}
          filters={healthFilters}
          pageStoreId={storeId}
          stores={stores?.availableStores || []}
          categories={catalog.data?.categories || []}
          categoriesLoading={catalog.isLoading}
          categoriesError={catalog.error}
          onCategoriesRetry={() => catalog.refetch()}
          onApplyFilters={setHealthFilters}
        />
        <RankingCard title="员工销售榜" type="employeeSalesRank" rows={employeeSalesReports.data?.employeeSalesRank} period={rankingPeriods.employeeSalesRank} onPeriodChange={(nextPeriod) => setRankingPeriod('employeeSalesRank', nextPeriod)} currency={currency} loading={employeeSalesReports.isLoading} error={employeeSalesReports.error} resolved={employeeSalesReports.isSuccess} onRetry={() => employeeSalesReports.refetch()} />
        <RankingCard title="员工回收榜" type="employeeRecoveryRank" rows={employeeRecoveryReports.data?.employeeRecoveryRank} period={rankingPeriods.employeeRecoveryRank} onPeriodChange={(nextPeriod) => setRankingPeriod('employeeRecoveryRank', nextPeriod)} currency={currency} loading={employeeRecoveryReports.isLoading} error={employeeRecoveryReports.error} resolved={employeeRecoveryReports.isSuccess} onRetry={() => employeeRecoveryReports.refetch()} />
        <RankingCard title="门店销售榜" type="storeSalesRank" rows={storeSalesReports.data?.storeSalesRank} period={rankingPeriods.storeSalesRank} onPeriodChange={(nextPeriod) => setRankingPeriod('storeSalesRank', nextPeriod)} currency={currency} selectedStoreId={storeId} loading={storeSalesReports.isLoading} error={storeSalesReports.error} resolved={storeSalesReports.isSuccess} onRetry={() => storeSalesReports.refetch()} />
        <RankingCard title="门店回收榜" type="storeRecoveryRank" rows={storeRecoveryReports.data?.storeRecoveryRank} period={rankingPeriods.storeRecoveryRank} onPeriodChange={(nextPeriod) => setRankingPeriod('storeRecoveryRank', nextPeriod)} currency={currency} selectedStoreId={storeId} loading={storeRecoveryReports.isLoading} error={storeRecoveryReports.error} resolved={storeRecoveryReports.isSuccess} onRetry={() => storeRecoveryReports.refetch()} />
      </div>
      <LogCenterPanel storeId={storeId} allowed={hasPermission('luxury:products:logs')} />
    </section>
  </main>
}

function CardState({ loading, error, denied, empty, onRetry, emptyText = '暂无数据' }: {
  loading?: boolean; error?: unknown; denied?: boolean; empty?: boolean
  onRetry: () => void; emptyText?: string
}) {
  if (loading) return <div className="report-card-state"><span className="spinner" /><strong>正在加载经营数据</strong></div>
  if (error) return <div className="report-card-state is-error"><WarningCircle size={24} /><strong>数据加载失败</strong><small>{error instanceof Error ? error.message : '请稍后重试'}</small><button type="button" onClick={onRetry}><ArrowClockwise size={14} />重新加载</button></div>
  if (denied) return <div className="report-card-state"><WarningCircle size={24} /><strong>无权查看此报表</strong><small>请联系管理员开通对应报表权限</small></div>
  if (empty) return <div className="report-card-state"><CheckCircle size={24} /><strong>{emptyText}</strong><small>当前筛选范围内没有可展示的数据</small></div>
  return null
}

function InventoryCard({ summary, dimension, onDimensionChange, loading, error, resolved, onRetry }: {
  summary?: Reports['inventorySummary']; dimension: InventoryDimension
  onDimensionChange: (value: InventoryDimension) => void
  loading: boolean; error: unknown; resolved: boolean; onRetry: () => void
}) {
  const rows = summary?.breakdown || []
  const values = rows.map((row) => Math.max(0, Number(row.totalValue || 0)))
  const totalValue = values.reduce((sum, value) => sum + value, 0)
  let cursor = 0
  const stops = rows.map((_, index) => {
    const start = cursor
    cursor += totalValue ? values[index]! / totalValue * 100 : 0
    return `${inventoryColor(index)} ${start}% ${cursor}%`
  })
  const donutStyle = {
    '--inventory-donut': totalValue ? `conic-gradient(${stops.join(',')})` : 'conic-gradient(#edf0f6 0 100%)',
  } as CSSProperties
  const LegendIcon = dimension === 'STORE' ? Storefront : Handbag

  return <article className="report-card inventory-card">
    <header className="report-head"><h3>库存统计</h3><ReportMenu value={dimension} options={inventoryDimensionOptions} onChange={onDimensionChange} ariaLabel="库存统计维度" icon={LegendIcon} /></header>
    <CardState loading={loading} error={error} denied={resolved && !summary} onRetry={onRetry} />
    {summary && !loading && !error && <div className="report-content" key={dimension}>
      <div className="inventory-kpis"><span><small>全部货值</small><strong>{money(summary.saleValue)}</strong></span><span><small>总数量</small><strong className="inventory-total-count">{summary.totalCount || 0}件</strong></span><span><small>我的库存成本</small><strong>{summary.costAmount == null ? '权限不可见' : money(summary.costAmount)}</strong></span></div>
      <div className="inventory-visual"><div className="inventory-donut" role="img" aria-label={`${dimension === 'STORE' ? '按门店' : '按分类'}库存货值构成`} style={donutStyle} /><ul className="inventory-legend-list" aria-label={`${dimension === 'STORE' ? '按门店' : '按分类'}库存明细，共${rows.length}项`} tabIndex={rows.length > 3 ? 0 : undefined}>{rows.length ? rows.map((row, index) => <li key={row.groupId || row.name || `inventory-${index}`} style={{ '--legend-color': inventoryColor(index) } as CSSProperties}><div className="inventory-legend-row"><LegendIcon size={16} weight="duotone" /><strong title={row.name}>{row.name}</strong><span>{row.totalCount}件</span><em>{money(row.totalValue)}</em></div></li>) : <li className="inventory-legend-empty"><Package size={18} /><span>暂无库存构成</span></li>}</ul></div>
    </div>}
  </article>
}

function HealthFilterChoice({ active, children, onClick }: {
  active: boolean; children: string; onClick: () => void
}) {
  return <button
    className={`activity-filter-choice${active ? ' active' : ''}`}
    type="button"
    role="radio"
    aria-checked={active}
    onClick={onClick}
  >{active && <Check size={12} weight="bold" />}{children}</button>
}

function HealthFilterDialog({
  filters, pageStoreId, stores, categories, categoriesLoading, categoriesError,
  returnFocusRef, onClose, onApply, onCategoriesRetry,
}: {
  filters: HealthFilters; pageStoreId: string; stores: StoreOption[]; categories: CategoryOption[]
  categoriesLoading: boolean; categoriesError: unknown
  returnFocusRef: React.RefObject<HTMLButtonElement | null>
  onClose: () => void; onApply: (filters: HealthFilters) => void; onCategoriesRetry: () => void
}) {
  const [draft, setDraft] = useState<HealthFilters>(() => ({ ...filters }))
  const [activeSection, setActiveSection] = useState<HealthFilterSection>('store')
  const dialogRef = useRef<HTMLElement>(null)
  const availableStores = pageStoreId
    ? stores.filter((store) => String(store.id) === pageStoreId)
    : stores
  const storeOptions = [
    ...(!pageStoreId ? [{ id: '', name: '全部门店' }] : []),
    ...availableStores,
  ]
  const categoryOptions: CategoryOption[] = [{ id: '', name: '全部分类' }, ...categories]
  const resetFilters: HealthFilters = { storeId: pageStoreId, categoryId: '' }

  useEffect(() => {
    const returnFocusTarget = returnFocusRef.current
    const selector = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    dialogRef.current?.querySelector<HTMLButtonElement>(selector)?.focus()
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
        return
      }
      if (event.key !== 'Tab') return
      const focusable = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>(selector) || [])
      if (!focusable.length) return
      const first = focusable[0]!
      const last = focusable[focusable.length - 1]!
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault(); last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      window.requestAnimationFrame(() => returnFocusTarget?.focus())
    }
  }, [onClose, returnFocusRef])

  return createPortal(<div className="activity-filter-backdrop health-filter-backdrop" role="presentation" onMouseDown={onClose}>
    <section ref={dialogRef} className="activity-filter-dialog health-filter-dialog" role="dialog" aria-modal="true" aria-labelledby="health-filter-dialog-title" onMouseDown={(event) => event.stopPropagation()}>
      <header className="activity-filter-dialog-head"><div><h3 id="health-filter-dialog-title">库存健康</h3><small>按门店和商品分类查看当前库存健康情况</small></div><button type="button" aria-label="关闭库存健康筛选" onClick={onClose}><X size={19} /></button></header>
      <div className="activity-filter-dialog-body">
        <nav role="tablist" aria-label="库存健康筛选项目">
          <button className={activeSection === 'store' ? 'active' : ''} type="button" role="tab" aria-selected={activeSection === 'store'} aria-controls="health-filter-panel" onClick={() => setActiveSection('store')}><span>按门店</span>{draft.storeId && <Check size={12} weight="bold" />}</button>
          <button className={activeSection === 'category' ? 'active' : ''} type="button" role="tab" aria-selected={activeSection === 'category'} aria-controls="health-filter-panel" onClick={() => setActiveSection('category')}><span>商品分类</span>{draft.categoryId && <Check size={12} weight="bold" />}</button>
        </nav>
        <div id="health-filter-panel" className="activity-filter-content health-filter-content" role="tabpanel">
          {activeSection === 'store' && <section>
            <div className="activity-filter-section-title"><strong>按门店</strong><small>单选</small></div>
            <p className="health-filter-guidance">选择一个门店，查看该门店在库商品的成本、寄卖货值与账龄分布。</p>
            <div className="activity-filter-choice-grid health-filter-choice-grid" role="radiogroup" aria-label="选择库存健康门店">{storeOptions.map((store) => <HealthFilterChoice active={draft.storeId === String(store.id)} key={String(store.id) || 'all-stores'} onClick={() => setDraft((current) => ({ ...current, storeId: String(store.id) }))}>{store.name}</HealthFilterChoice>)}</div>
          </section>}
          {activeSection === 'category' && <section>
            <div className="activity-filter-section-title"><strong>商品分类</strong><small>单选</small></div>
            <p className="health-filter-guidance">分类口径与 APP 保持一致，仅统计当前分类下的商品。</p>
            <div className="activity-filter-choice-grid health-filter-choice-grid" role="radiogroup" aria-label="选择库存健康商品分类">{categoryOptions.map((category) => <HealthFilterChoice active={draft.categoryId === String(category.id)} key={String(category.id) || 'all-categories'} onClick={() => setDraft((current) => ({ ...current, categoryId: String(category.id) }))}>{category.name}</HealthFilterChoice>)}</div>
            {categoriesLoading && <div className="health-filter-options-state"><span className="spinner" />正在加载商品分类</div>}
            {Boolean(categoriesError) && <div className="health-filter-options-state is-error"><span>商品分类加载失败</span><button type="button" onClick={onCategoriesRetry}>重新加载</button></div>}
          </section>}
        </div>
      </div>
      <footer className="activity-filter-dialog-foot health-filter-dialog-foot"><button type="button" onClick={() => setDraft(resetFilters)}>重置</button><button type="button" onClick={() => onApply({ ...draft })}>应用筛选</button></footer>
    </section>
  </div>, document.body)
}

function HealthCard({
  health, loading, error, resolved, onRetry, filters, pageStoreId, stores, categories,
  categoriesLoading, categoriesError, onCategoriesRetry, onApplyFilters,
}: {
  health?: Reports['inventoryHealth']; loading: boolean; error: unknown; resolved: boolean; onRetry: () => void
  filters: HealthFilters; pageStoreId: string; stores: StoreOption[]; categories: CategoryOption[]
  categoriesLoading: boolean; categoriesError: unknown; onCategoriesRetry: () => void
  onApplyFilters: (filters: HealthFilters) => void
}) {
  const [filterOpen, setFilterOpen] = useState(false)
  const filterTriggerRef = useRef<HTMLButtonElement>(null)
  const closeFilter = useCallback(() => setFilterOpen(false), [])
  const activeFilterCount = Number(Boolean(filters.storeId)) + Number(Boolean(filters.categoryId))
  const ages = [health?.within30Days || 0, health?.days31To90 || 0, health?.over90Days || 0]
  const total = ages.reduce((sum, value) => sum + value, 0)
  const first = total ? Math.round(ages[0]! / total * 100) : 0
  const second = total ? Math.round(ages[1]! / total * 100) : 0
  const percentages = [first, second, total ? Math.max(0, 100 - first - second) : 0]

  return <>
    <article className="report-card health-card">
      <header className="report-head"><h3>库存健康</h3><button ref={filterTriggerRef} className={`health-filter-trigger${activeFilterCount ? ' active' : ''}`} type="button" aria-haspopup="dialog" aria-expanded={filterOpen} aria-label={activeFilterCount ? `库存健康筛选，已启用${activeFilterCount}个条件` : '库存健康筛选'} onClick={() => setFilterOpen(true)}><Funnel size={13} weight={activeFilterCount ? 'fill' : 'regular'} /><span>筛选</span>{activeFilterCount > 0 && <em>{activeFilterCount}</em>}</button></header>
      <CardState loading={loading} error={error} denied={resolved && !health} onRetry={onRetry} />
      {health && !loading && !error && <div className="report-content" key={`${filters.storeId}-${filters.categoryId}`}>
        <div className="health-kpis"><span><small>我的成本</small><strong>{health.ownCost == null ? '权限不可见' : money(health.ownCost)}</strong></span><span><small>寄卖货值</small><strong>{money(health.consignmentValue)}</strong></span></div>
        <div className="age-block"><header><strong>库存账龄分布</strong><small>共 {total} 件</small></header>{total ? <div className="age-bar" role="img" aria-label={`30天内${percentages[0]}%，31至90天${percentages[1]}%，90天以上${percentages[2]}%`} style={{ gridTemplateColumns: `${percentages[0]}fr ${percentages[1]}fr ${percentages[2]}fr` }}>{percentages.map((percentage, index) => <i key={index}>{percentage >= 8 ? `${percentage}%` : ''}</i>)}</div> : <div className="age-bar is-empty"><span>暂无账龄数据</span></div>}<div className="age-legend"><span><i className="fresh" />30天内 <b>{percentages[0]}%</b></span><span><i className="watch" />31–90天 <b>{percentages[1]}%</b></span><span><i className="risk" />90天以上 <b>{percentages[2]}%</b></span></div></div>
      </div>}
    </article>
    {filterOpen && <HealthFilterDialog filters={filters} pageStoreId={pageStoreId} stores={stores} categories={categories} categoriesLoading={categoriesLoading} categoriesError={categoriesError} returnFocusRef={filterTriggerRef} onClose={closeFilter} onCategoriesRetry={onCategoriesRetry} onApply={(nextFilters) => { onApplyFilters(nextFilters); closeFilter() }} />}
  </>
}

function RankingCard({
  title, type, rows, period, onPeriodChange, currency, selectedStoreId = '', loading, error, resolved, onRetry,
}: {
  title: string; type: RankType; rows?: Rank[]; period: ReportPeriod
  onPeriodChange: (value: ReportPeriod) => void; currency: string; selectedStoreId?: string
  loading: boolean; error: unknown; resolved: boolean; onRetry: () => void
}) {
  const employee = type.startsWith('employee')
  const recovery = type.toLowerCase().includes('recovery')
  const scopedRows = (rows || []).filter((row) => employee || !selectedStoreId || String(row.id) === selectedStoreId)
  const visibleRows = scopedRows.slice(0, employee ? 8 : 3)
  const maximum = Math.max(1, ...visibleRows.map((row) => Number(row.amount || 0)))

  return <article className={`report-card ${employee ? 'ranking-card' : 'store-ranking-card'}`}>
    <header className="report-head"><h3>{title}</h3><ReportMenu value={period} options={reportPeriodOptions} onChange={onPeriodChange} ariaLabel={`${title}周期`} /></header>
    <CardState loading={loading} error={error} denied={resolved && rows === undefined} empty={resolved && rows !== undefined && visibleRows.length === 0} emptyText="暂无排行数据" onRetry={onRetry} />
    {visibleRows.length > 0 && !loading && !error && <div className="report-content" key={`${period}-${selectedStoreId}`}>
      {employee ? <><div className="ranking-labels"><span aria-hidden="true" /><span>员工</span><span><b>{recovery ? '回收金额' : '销售额'}</b><b>件数</b></span></div><div className="ranking-list">{visibleRows.map((row, index) => <div className="ranking-row" key={row.id}><span className={`rank rank-${index + 1}`}>{index + 1}</span><span className="rank-person"><span className={`rank-avatar tone-${index % 2 ? 'violet' : 'blue'}`}><User size={16} weight="duotone" aria-hidden="true" /></span><span className="rank-person-name"><strong>{row.name || '未命名'}</strong></span></span><span className="rank-values"><b>{money(row.amount, row.currency || currency)}</b><em>{row.quantity}件</em><i><u style={{ '--bar-width': `${Math.max(4, Math.round(Number(row.amount || 0) / maximum * 100))}%` } as CSSProperties} /></i></span></div>)}</div></> : <div className="store-ranking-list">{visibleRows.map((row, index) => <div className="store-rank-row" key={row.id}><span className={`rank rank-${index + 1}`}>{index + 1}</span><strong>{row.name || '未命名'}</strong><span><b>{money(row.amount, row.currency || currency)}</b><small>件数 {row.quantity}件</small><i><u style={{ '--bar-width': `${Math.max(4, Math.round(Number(row.amount || 0) / maximum * 100))}%` } as CSSProperties} /></i></span></div>)}</div>}
    </div>}
  </article>
}
